import type { SearchPageState } from "../redux/types";
import { BreadcrumbType } from "../../../utils/models";
import { selectBeSortCodeNested } from "../redux/searchSelectors";
import { type SearchResultsDTO } from "../models/SearchResultsDTO";
import { getSearchPageType } from "~/features/search/utils/searchPageUtils";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";

export function getSearchEndpointParams(
  state: SearchPageState,
  preselectCategories?: boolean,
  pageType?: SearchPageType,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set("site", "schaeffler");

  searchParams.set("text", state.searchParams.text);
  searchParams.set("page", state.searchParams.currentPage.toString());

  if (state.searchParams.showOnlyCampaignResults) {
    searchParams.append("campaignResultsOnly", "true");
  }

  if (pageType === SearchPageType.Campaign) {
    searchParams.append("campaignPage", "true");
  }

  if (state.searchParams.showOnlyRelevant) {
    searchParams.set("showOnlyRelevant", "true");
  }

  const selectedSort = selectBeSortCodeNested(state);
  if (selectedSort) {
    searchParams.set("sort", selectedSort);
  }

  const qParts = [];
  if (state.facets.breadcrumbs.length > 0) {
    const techValueParts: string[] = [];

    for (const bc of state.facets.breadcrumbs) {
      switch (bc.type) {
        case BreadcrumbType.FACET: {
          qParts.push(`${bc.facetCode}:${encodeURIComponent(bc.facetValueCode)}`);
          break;
        }

        case BreadcrumbType.TECH_VALUE: {
          techValueParts.push(`${bc.code}_${bc.valueType}:${bc.value}`);
          break;
        }

        default:
          throw new Error("Unsupported breadcrumb type!");
      }
    }

    if (techValueParts.length > 0) {
      searchParams.set("techValues", techValueParts.join(","));
    }
  }

  if (preselectCategories) {
    searchParams.set("preselectCategory", "true");
  } else {
    let selectedProductGroups = state.facets.selectedProductGroups;
    if (pageType !== SearchPageType.PLP && selectedProductGroups.length > 1) {
      selectedProductGroups = selectedProductGroups.slice(1);
    }
    selectedProductGroups.forEach((group) => qParts.push(`categoryPath:${group}`));
    if (state.facets.isSelectionAssistant && state.facets.selectedProductGroups.length === 0) {
      qParts.push("categoryPath:/ROTARY");
    }
  }

  if (qParts.length > 0) {
    searchParams.set("q", `${state.searchParams.text}:${selectedSort}:${qParts.join(":")}`);
  } else {
    searchParams.set("q", `${state.searchParams.text}:${selectedSort}`);
  }

  if (state.facets.isSelectionAssistant) {
    searchParams.set("isSelectionAssistant", "ROTARY");
  }

  if (state.facets.assistantValues.length > 0) {
    searchParams.set("assistantValues", state.facets.assistantValues.join(","));
  }

  return searchParams;
}

const getBaseUrlToCall = (pageType: SearchPageType) => {
  if (pageType === SearchPageType.PLP) {
    const pathname = window.location.pathname;
    const contextPath = window.__SCHA_BOOT__?.contextPath ?? "";
    const relativePath = pathname.startsWith(contextPath) ? pathname.substring(contextPath.length) : pathname;
    const parts = relativePath.split("/").filter(Boolean);
    const segment = parts[0];
    const categoryCode = parts.slice(1).join("/");
    return occUrl(`/${segment}/${categoryCode}/pagedata`);
  } else {
    return occUrl("/search/searchpage/pagedata");
  }
};

let abortController: AbortController | null = null;

export async function fetchSearchResults(
  state: SearchPageState,
  preselectCategories?: boolean,
): Promise<SearchResultsDTO | false> {
  const pageType = getSearchPageType();
  const searchParams = getSearchEndpointParams(state, preselectCategories, pageType);
  const baseUrl = getBaseUrlToCall(pageType);

  const url = `${baseUrl}?${searchParams.toString()}`;

  abortController?.abort();
  abortController = new AbortController();

  const response = await occFetch(url, { signal: abortController.signal });

  if (response.redirected) {
    window.location.reload();
  }

  if (!response.ok) {
    return false;
  }

  return response.json();
}
